import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FiCalendar, FiUsers } from 'react-icons/fi';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import PageTitle from '../../components/PageTitle';

/**
 * BlogPost interface for Firestore blogPost collection.
 */
interface BlogPost {
  id: string;
  title: string;
  content: string;
  authorId: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  scheduledAt: Timestamp;
  isPublished: boolean;
  tags: string[];
  coverImageUrl?: string;
  excerpt?: string;
}


const EventsPage: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const heroRef = useScrollAnimation();
  const eventsRef = useScrollAnimation();

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setLoading(true);

        const postsQuery = query(
          collection(db, 'blogPost'),
          where('isPublished', '==', true)
        );
        const querySnapshot = await getDocs(postsQuery);
        console.log('Fetched blogPost docs:', querySnapshot.docs.map(d => d.data()));
        const fetchedBlogPosts = querySnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              tags: Array.isArray(data.tags) ? data.tags : [],
            } as BlogPost;
          })
          .sort((a, b) => b.scheduledAt.toMillis() - a.scheduledAt.toMillis());
        setBlogPosts(fetchedBlogPosts);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchBlogPosts();
  }, []);

  const formatDate = (timestamp: Timestamp) => {
    try {
      return format(timestamp.toDate(), 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date not available';
    }
  };



  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <PageTitle
        title="Future with Solar Energy"
        subtitle="Explore how solar panels are changing the way we power our homes, businesses, and planet—one sunbeam at a time."
      />

      {/* Events Section */}
      <div 
        ref={eventsRef}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        {loading ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-800 rounded-lg overflow-hidden">
                  <div className="aspect-video bg-gray-700" />
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-700 rounded w-3/4" />
                    <div className="h-4 bg-gray-700 rounded w-full" />
                    <div className="h-4 bg-gray-700 rounded w-full" />
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-700 rounded w-1/2" />
                      <div className="h-4 bg-gray-700 rounded w-2/3" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : blogPosts.length === 0 ? (
          <div className="text-center py-12">
            <FiCalendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-white">No blog posts available</h3>
            <p className="mt-1 text-gray-400">Check back later for new blog posts.</p>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition duration-300 group"
              >
                <div className="aspect-video bg-gray-700 relative overflow-hidden">
                  {post.coverImageUrl ? (
                    <img
                      src={post.coverImageUrl}
                      alt={post.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FiCalendar className="w-12 h-12 text-gray-600" />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white group-hover:text-indigo-400 transition duration-300">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-gray-400 line-clamp-2">
                    {post.excerpt || post.content.slice(0, 120) + (post.content.length > 120 ? '...' : '')}
                  </p>
                  <div className="mt-4 space-y-2 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <FiCalendar className="w-4 h-4 text-indigo-400" />
                      <span>{formatDate(post.scheduledAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiUsers className="w-4 h-4 text-indigo-400" />
                      <span>By {post.authorId}</span>
                    </div>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {post.tags.map((tag) => (
                          <span key={tag} className="bg-indigo-700 text-xs px-2 py-0.5 rounded-full text-white">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
